import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Send } from "lucide-react";

interface TestEmailDialogProps {
  onTest: (email: string) => void;
  testing: boolean;
  disabled: boolean;
}

export default function TestEmailDialog({ onTest, testing, disabled }: TestEmailDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = useCallback(() => {
    if (!email.trim()) {
      setError("E-mail é obrigatório");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Por favor, insira um endereço de e-mail válido");
      return;
    }
    setError("");
    onTest(email);
  }, [email, onTest]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setEmail("");
      setError("");
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="rounded-none uppercase tracking-widest text-xs px-8 py-3 border-primary-200 dark:border-primary-800"
        >
          <Send className="h-4 w-4 mr-2" />
          {testing ? "Testando..." : "Enviar E-mail de Teste"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Enviar E-mail de Teste</DialogTitle>
          <DialogDescription>
            Informe o endereço de e-mail que receberá a mensagem de teste.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="test-email" className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              E-mail de Destino
            </Label>
            <Input
              id="test-email"
              type="email"
              placeholder="seu-email@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none border-primary-200 dark:border-primary-800"
            />
            {error && (
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            )}
          </div>
        </div>
        <DialogFooter className="mt-6">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
            >
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={testing}
            onClick={handleSubmit}
            className="rounded-none uppercase tracking-widest text-xs"
          >
            {testing ? "Enviando..." : "Enviar Teste"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
